import Point from "@arcgis/core/geometry/Point";
import Graphic from "esri/Graphic";
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import styled from "styled-components";
import FilledButton, {
  FilledButtonAlt,
} from "../../components/atoms/FilledButton";
import Callout from "../../components/molecules/Callout";
import {
  useFeaturesContext,
  useFeaturesDispatchContext,
  useMapContext,
  useNvrToolModeContext,
} from "../../context/ContextProvider";
import { IState } from "../../context/featuresReducer";
import colours from "../../lib/colours";
import {
  cmTypeHelp,
  defaultTreeBenchmarkCm,
  noScatteredTreeAllowed,
  scatteredTreeOffsetBenchmarkThreshold,
  sizesHelp,
} from "../../lib/constants";
import {
  Patch,
  Tree,
  featureSource,
  nvrToolModes,
  nvrTools,
  treeCmType,
  treeSize,
  treeType,
} from "../../lib/types";
import {
  getLTBenchmarks,
  getOverlappingFeatures,
} from "../../utils/featureUtils";
import Badge from "../../components/atoms/Badge";
import Map from "@arcgis/core/Map";
import SpinnerBase from "../../components/atoms/SpinnerBase";
import mapConfig from "../../lib/mapConfig";
import { UnitSpan } from "../../components/atoms/Text";
import { TextInput } from "../../components/atoms/TextInput";
import {
  StyledRadioContainer,
  StyledRadioInput,
  StyledRadioLabel,
} from "../../components/molecules/RadioButtons";
import { checkTreeSizeAboveThreshold } from "../../utils/validations";

const Container = styled.div`
  color: ${colours.th1};
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const Title = styled.h3`
  margin-top: 4px;
  font-size: 20px;
  font-weight: 600;
`;

const Subtitle = styled.h4`
  margin-top: 40px;
  margin-bottom: 0px;
  font-weight: 600;
`;

const StyledContentContainer = styled.div`
  text-align: left;
  overflow: auto;
  padding-block: 20px;
  width: 100%;
`;

const StyledSelect = styled.select`
  height: 36px;
  font-size: 16px;
  font-family: "vic";
  font-weight: 500;
  background-color: ${colours.white};
  border: 1px solid ${colours.grey1};
  border-radius: 4px;
  padding-right: 20px;
  padding-left: 4px;
`;

const StyledProgressContainer = styled.div`
  display: flex;
  align-items: center;
  padding-block: 12px;
  color: ${colours.th1l1};
`;

const StyledSpeciesContainer = styled.div`
  padding-block: 12px;
`;

const StyledRadioGroupContainer = styled.div`
  margin-block: 12px;
`;

const StyledBadgeContainer = styled.div`
  margin-left: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 4px;
`;

const SizeHintContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
`;

const HintText = styled.span`
  font-style: italic;
  color: ${colours.grey1};
`;

const StyledInput = styled(TextInput)`
  width: 96px;
`;

const StyledFooterContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: row-reverse;
`;

const spinnerStyle = {
  display: "flex",
  alignItems: "center",
  marginLeft: "6px",
};

const spinnerChildrenStyle = {
  width: "8px",
  height: "8px",
};

const cmTypeText = {
  [treeCmType.CIR]: "Circumference",
  [treeCmType.DBH]: "Diameter at Breast Height",
};

interface Benchmark {
  name?: string;
  cm: number;
}

interface IProps {
  onClose: any;
  onSubmit: any;
  tree?: Tree;
  graphic?: Graphic;
}

const tableConfig = mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;

const TreeProperties = ({ onClose, onSubmit, tree, graphic }: IProps) => {
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const { map }: { map: Map } = useMapContext();
  const state: IState = useFeaturesContext();
  const dispatch = useFeaturesDispatchContext();
  const [type, setType] = useState<treeType>(treeType.Scattered);
  const [allowScattered, setAllowScattered] = useState<boolean>(true);
  const [benchmarkOptions, setBenchmarkOptions] = useState<any[]>();
  const [benchmark, setBenchmark] = useState<Benchmark>();
  const [cmType, setCmType] = useState<treeCmType>(treeCmType.CIR);
  const [cm, setCm] = useState<string>("");
  const [cmValid, setCmValid] = useState<boolean>(true);
  const [isLargeTree, setIsLargeTree] = useState<boolean>(false);
  const [offsetBenchmarkThreshold, setOffsetBenchmarkThreshold] =
    useState<number>(scatteredTreeOffsetBenchmarkThreshold);
  const [validOffsetTree, setValidOffsetTree] = useState<boolean>(true);
  const [
    validOffsetTreeCalculationPending,
    setValidOffsetTreeCalculationPending,
  ] = useState<boolean>(false);
  const [vegCode, setVegCode] = useState<string>();

  const validOffsetTreeTimeout = useRef<any>();

  const newTree: boolean = !tree;

  const findStartingBenchmark = useCallback(
    (benchmarks: any[]) => {
      if (!tree || !tree.benchmark) {
        return benchmarks[0];
      } else {
        return benchmarks.filter(
          (x) => x[tableConfig.speciesField] === tree.benchmark
        )[0];
      }
    },
    [tree]
  );

  // initialise form
  useEffect(() => {
    const treeGeom = (tree ? new Point(tree.geom) : graphic?.geometry) as Point;
    let startingTreeType = tree ? tree.type : treeType.Scattered;
    const overlappingPatches = getOverlappingFeatures(
      treeGeom as Point,
      state.patchesList.map((x: string) => state.features[x] as Patch)
    );
    if (overlappingPatches.length) {
      startingTreeType = treeType.Patch;
      setAllowScattered(false);
      setOffsetBenchmarkThreshold(1);
    }
    setType(startingTreeType);
    setCmType(tree ? tree.cmType : treeCmType.CIR);
    setCm(tree ? String(tree.cm) : "");
    getLTBenchmarks(treeGeom, map).then((benchmarkData) => {
      if (!benchmarkData || !benchmarkData.evc) {
        setTimeout(() => {
          setBenchmark({
            cm: defaultTreeBenchmarkCm,
          });
        }, 200);
        return;
      }
      const benchmarks = benchmarkData.ltBenchmarks;
      setTimeout(() => {
        setVegCode(benchmarkData.evc);
        setBenchmarkOptions(benchmarks);
        if (benchmarks) {
          const benchmark: any = findStartingBenchmark(benchmarks);
          setBenchmark({
            name: benchmark[tableConfig.speciesField],
            cm: Number(benchmark[tableConfig.benchmarkField]),
          });
        }
      }, 200);
    });
  }, [state, tree, graphic, map, findStartingBenchmark]);

  // react to any changes to recalculate tree size
  useEffect(() => {
    if (!cmValid && nvrToolMode === nvrTools.offset) {
      setValidOffsetTree(true);
      setValidOffsetTreeCalculationPending(false);
      if (validOffsetTreeTimeout.current) {
        clearTimeout(validOffsetTreeTimeout.current);
      }
    }
    if (benchmark && benchmark.cm && cm && cmValid) {
      const treeCIR =
        cmType === treeCmType.CIR ? Number(cm) : Number(cm) * Math.PI;
      setIsLargeTree(treeCIR >= benchmark.cm);
      if (nvrToolMode === nvrTools.offset) {
        if (validOffsetTreeTimeout.current) {
          clearTimeout(validOffsetTreeTimeout.current);
        }
        validOffsetTreeTimeout.current = setTimeout(() => {
          setValidOffsetTree(
            checkTreeSizeAboveThreshold(
              treeCIR,
              benchmark.cm,
              offsetBenchmarkThreshold
            )
          );
          setValidOffsetTreeCalculationPending(false);
        }, 1000);
        setValidOffsetTreeCalculationPending(true);
      }
    }
  }, [
    state,
    cm,
    cmType,
    benchmark,
    cmValid,
    nvrToolMode,
    offsetBenchmarkThreshold,
    validOffsetTreeTimeout,
  ]);

  const onTreeTypeChange = (e: any) => {
    setType(e.target.value);
  };

  // validate cm value
  useEffect(() => {
    const isValid = !["", "0"].includes(cm) && Number(cm) > 0;
    setCmValid(isValid);
  }, [cm, setCmValid]);

  const onBenchmarkChange = (e: any) => {
    const name = e.target.value;
    const selectedBenchmark = benchmarkOptions?.filter(
      (x) => x[tableConfig.speciesField] === name
    )[0];
    setBenchmark({
      name: selectedBenchmark[tableConfig.speciesField],
      cm: selectedBenchmark[tableConfig.benchmarkField],
    });
  };

  const onRadioChange = (e: any) => {
    setCmType(e.target.value);
  };

  const onCmChange = (e: any) => {
    setCm(e.target.value);
  };

  const saveTree = () => {
    const feature: any = {
      source: tree?.source || featureSource.Drawn,
      geom: tree ? tree.geom : graphic?.geometry.toJSON(),
      type: type,
      cm: Number(cm),
      cmType: cmType,
      size: isLargeTree ? treeSize.Lg : treeSize.Sm,
      vegCode: vegCode,
      benchmark: benchmark?.name,
      benchmarkCm: benchmark?.cm,
    };
    if (tree) {
      feature.id = tree.id;
    }
    dispatch({
      type: tree ? "updateTree" : "addTree",
      payload: feature,
    });
  };

  const onConfirm = () => {
    saveTree();
    onSubmit();
  };

  const title = !!tree ? "Edit Tree " + tree.id : "New Tree";
  const sizeText = isLargeTree ? treeSize.Lg : treeSize.Sm;

  const isFormSubmitDisabled = useMemo(() => {
    if (nvrToolMode === nvrTools.offset) {
      return !cmValid || validOffsetTreeCalculationPending || !validOffsetTree;
    } else {
      return !cmValid;
    }
  }, [
    cmValid,
    nvrToolMode,
    validOffsetTree,
    validOffsetTreeCalculationPending,
  ]);

  const renderSpeciesControl = () => {
    if (!benchmarkOptions) return;
    return (
      <StyledSpeciesContainer>
        <Subtitle>Tree benchmark</Subtitle>
        <p style={{ marginBottom: "6px" }}>Select genus/species</p>
        <StyledSelect value={benchmark?.name} onChange={onBenchmarkChange}>
          {benchmarkOptions.map((x: any, index) => (
            <option key={index} value={x[tableConfig.speciesField]}>
              {x[tableConfig.speciesField]}
            </option>
          ))}
        </StyledSelect>
      </StyledSpeciesContainer>
    );
  };

  if (!benchmark)
    return (
      <StyledProgressContainer>
        <span>Fetching benchmark options</span>
        <SpinnerBase
          style={spinnerStyle}
          childrenStyle={spinnerChildrenStyle}
        />
      </StyledProgressContainer>
    );

  return (
    <Container>
      <Title>{title}</Title>
      <StyledContentContainer>
        <Subtitle style={{ marginBlock: "0 16px" }}>Tree type</Subtitle>
        <StyledSelect
          value={type}
          disabled={!allowScattered || nvrToolMode === nvrTools.offset}
          onChange={onTreeTypeChange}
        >
          {((nvrToolMode === nvrTools.offset && !allowScattered) ||
            nvrToolMode !== nvrTools.offset) && (
            <option value={treeType.Patch}>Patch Tree</option>
          )}
          {allowScattered && (
            <option value={treeType.Scattered}>Scattered Tree</option>
          )}
        </StyledSelect>
        {!allowScattered && (
          <div style={{ marginTop: "12px" }}>
            <Callout type="info">{noScatteredTreeAllowed}</Callout>
          </div>
        )}

        {benchmarkOptions && benchmarkOptions.length > 1
          ? renderSpeciesControl()
          : null}

        <Subtitle>Tree size</Subtitle>
        <StyledRadioGroupContainer>
          <StyledRadioContainer>
            <StyledRadioInput
              type="radio"
              id={treeCmType.CIR}
              name="cmType"
              value={treeCmType.CIR}
              onChange={onRadioChange}
              checked={cmType === treeCmType.CIR}
            />
            <StyledRadioLabel htmlFor={treeCmType.CIR}>
              {cmTypeText[treeCmType.CIR]}
              <UnitSpan>(cm)</UnitSpan>
              <StyledBadgeContainer>
                <Badge
                  color={colours[treeCmType.CIR]}
                  helpText={cmTypeHelp[treeCmType.CIR]}
                >
                  {treeCmType.CIR}
                </Badge>
              </StyledBadgeContainer>
            </StyledRadioLabel>
          </StyledRadioContainer>
          <StyledRadioContainer>
            <StyledRadioInput
              type="radio"
              id={treeCmType.DBH}
              name="cmType"
              value={treeCmType.DBH}
              onChange={onRadioChange}
              checked={cmType === treeCmType.DBH}
            />
            <StyledRadioLabel htmlFor={treeCmType.DBH}>
              {cmTypeText[treeCmType.DBH]}
              <UnitSpan>(cm)</UnitSpan>
              <StyledBadgeContainer>
                <Badge
                  color={colours[treeCmType.DBH]}
                  helpText={cmTypeHelp[treeCmType.DBH]}
                >
                  {treeCmType.DBH}
                </Badge>
              </StyledBadgeContainer>
            </StyledRadioLabel>
          </StyledRadioContainer>
        </StyledRadioGroupContainer>

        <InputContainer>
          <StyledInput
            autoFocus={newTree}
            type="number"
            min={1}
            placeholder="Enter cm"
            value={cm}
            onChange={onCmChange}
          ></StyledInput>
          {cmValid &&
            (nvrToolMode === nvrTools.removal ||
              (nvrToolMode === nvrTools.offset && isLargeTree)) && (
              <SizeHintContainer>
                <HintText>{isLargeTree ? "Large Tree" : "Small Tree"}</HintText>
                <StyledBadgeContainer>
                  <Badge
                    color={colours[sizeText]}
                    helpText={sizesHelp[sizeText]}
                  >
                    {sizeText}
                  </Badge>
                </StyledBadgeContainer>
              </SizeHintContainer>
            )}
        </InputContainer>
        {nvrToolMode === nvrTools.offset && !validOffsetTree && (
          <div style={{ marginTop: "16px" }}>
            <Callout type="warning">
              This tree is too small and does not meet the offset requirements
              for{" "}
              {offsetBenchmarkThreshold === 1
                ? "trees in patches"
                : "Scattered Trees"}
              .
            </Callout>
          </div>
        )}
      </StyledContentContainer>
      <StyledFooterContainer>
        <FilledButtonAlt onClick={onClose}>Cancel</FilledButtonAlt>
        <FilledButton
          disabled={isFormSubmitDisabled}
          onClick={onConfirm}
          style={{ marginRight: "10px", width: "96px" }}
        >
          {validOffsetTreeCalculationPending ? (
            <SpinnerBase
              style={{
                justifyContent: "center",
                width: "auto",
                ...spinnerStyle,
              }}
              childrenStyle={spinnerChildrenStyle}
            />
          ) : (
            "Confirm"
          )}
        </FilledButton>
      </StyledFooterContainer>
    </Container>
  );
};

export default TreeProperties;
